/** @format */
import { Injectable } from '@angular/core';
import { ValidatablePlan } from 'common-typescript';
import {
    Attainment,
    AttainmentGroupNode,
    AttainmentNode,
    CourseUnit,
    CourseUnitAttainment,
    CustomCourseUnitAttainment,
    CustomModuleAttainment,
    CustomStudyDraft,
    DegreeProgrammeAttainment,
    Module,
    ModuleAttainment,
    ModuleContent,
    OtmId,
} from 'common-typescript/types';
import * as _ from 'lodash-es';
import { DowngradedService, ServiceDowngradeMappings, StaticMembers } from 'sis-common';
import { LogService } from '../../error-handler/log.service';
import { sisuChain } from '../../util/utils';
import { isCourseUnitAttainment, isDegreeProgrammeAttainment, isModuleAttainment } from '../AttainmentUtil';
import {
    CourseUnitsById,
    IndexedAttainments,
    isCourseUnitSelection,
    isCustomCourseUnitSelection,
    isCustomModuleSelection,
    isCustomStudyDraftSelection,
    isModuleSelection,
    ModulesById,
    ModuleSelectionWithOrdering,
    PlanWithOrdering,
    SelectionItem,
    StudySelection,
} from './AttainmentTreeTypes';

@StaticMembers<DowngradedService>()
@Injectable()
export class AttainmentTreeService {
    static downgrade: ServiceDowngradeMappings = {
        moduleName: 'sis-components.attainment.editableTree.attainmentTreeServiceNg',
        serviceName: 'attainmentTreeService',
    };

    constructor(private logService: LogService) {}

    /**
     * Construct object having maps where attainments are indexed by different ids. This is done to avoid linear complexity queries
     * during otherwise needed for many of the methods.
     * byId: map of attainments with attainment id as key - useful for custom attainments
     * byCourseUnitGroupId: map of course unit attainments with course unit id as key - useful for course unit attainments
     * byModuleGroupId: map of module and degree programme attainments with module id as key - useful for module attainments
     * byCustomStudyDraftId: map of attainments created from study drafts with custom study draft id as key - useful for custom
     * attainments made from study drafts
     *
     * @param attainments array of attainments
     * @returns IndexedAttainments
     */
    indexedAttainmentsFrom(attainments: Attainment[]): IndexedAttainments {
        const byId = _.keyBy(attainments, 'id');
        const byCourseUnitGroupId = _.keyBy(
            attainments.filter(att => isCourseUnitAttainment(att)).map(att => att as CourseUnitAttainment),
            'courseUnitGroupId',
        );
        const byModuleGroupId = _.keyBy(
            attainments
                .filter(att => isModuleAttainment(att) || isDegreeProgrammeAttainment(att))
                .map(att => att as ModuleAttainment | DegreeProgrammeAttainment),
            'moduleGroupId',
        );
        const byCustomStudyDraftId = _.keyBy(_.filter(attainments, 'customStudyDraftId'), 'customStudyDraftId');
        return {
            byId,
            byCourseUnitGroupId,
            byModuleGroupId,
            byCustomStudyDraftId,
        };
    }

    /**
     * This method returns a deep clone of moduleContent that has ordering properties of selections initialized.
     *
     * @param validatablePlan used for initializing ordering
     * @param moduleId the id of "base module"
     * @param moduleContentOriginal the original plan
     * @param modulesById every module from referenced by moduleContentOriginal
     * @param indexedAttainments object created by indexedAttainmentsFrom
     * @returns Enriched copy of moduleContentOriginal
     */
    initModuleContentForTree(
        validatablePlan: ValidatablePlan,
        moduleId: OtmId,
        moduleContentOriginal: ModuleContent,
        modulesById: ModulesById,
        indexedAttainments: IndexedAttainments,
    ) {
        if (!moduleContentOriginal) {
            return undefined;
        }
        const copy = _.cloneDeep(moduleContentOriginal);
        const moduleContent: PlanWithOrdering = {
            courseUnitSelections: copy.courseUnitSelections,
            customCourseUnitAttainmentSelections: copy.customCourseUnitAttainmentSelections,
            customModuleAttainmentSelections: copy.customModuleAttainmentSelections,
            customStudyDrafts: copy.customStudyDrafts,
            moduleSelections: copy.moduleSelections,
        };
        const baseModuleSelection = _.find(moduleContent.moduleSelections, { moduleId });
        if (!!validatablePlan) {
            // Only add ordering if validatablePlan exists that can be used to order stuff...
            this.initOrderForSelection(baseModuleSelection, moduleContent, validatablePlan, modulesById, indexedAttainments, 0);
        } else {
            // ...otherwise let index decide the ordering
            getStudySelections(moduleContent).forEach((selection, index) => (selection.ordering = index));
        }
        return moduleContent;
    }

    /**
     * Returns selection items that are direct (one level) children of moduleSelection while taking visibility into account.
     * A child that is hidden will result into all it's children. For hidden grouping module this returns empty array.
     * This method will expand study selection having non empty substitutedBy.
     * If moduleNotAttained is true for child (module selection) then it is omitted in response.
     *
     * @param moduleSelection module selection for which we need children
     * @param moduleContent the plan like object where the potential children are defined
     * @returns array of study selection items having 'type' (GroupingModuleLike/Attainment), 'studySelection' and 'ordering'
     */
    getChildrenVisibleUnderModule(moduleSelection: ModuleSelectionWithOrdering, moduleContent: PlanWithOrdering): SelectionItem[] {
        if (moduleSelection.hidden) {
            return [];
        }
        const closestVisibleParentOrSelf = sisuChain(moduleContent.moduleSelections)
            .keyBy('moduleId')
            .mapValues(findVisibleParentIdIfHidden)
            .value();

        const selections: StudySelection[] = _.filter(
            getStudySelections(moduleContent),
            studySelection => moduleSelection.moduleId === _.get(closestVisibleParentOrSelf, studySelection.parentModuleId),
        );
        const resultSelections: SelectionItem[] = selections.flatMap(studySelection => {
            if (isModuleSelection(studySelection)) {
                if (_.has(studySelection, 'hidden')) {
                    const item: SelectionItem = { studySelection, type: 'GroupingModuleLike', ordering: studySelection.ordering };
                    return [item];
                }
                if (studySelection.moduleNotAttained) {
                    // some modules might be empty and do not have an attainment.
                    return [];
                }
            }
            if (isCourseUnitSelection(studySelection) && !_.isEmpty(studySelection.substitutedBy)) {
                // course unit selection with substitutions
                return _.map(_.range(studySelection.substitutedBy.length), i => ({
                    studySelection,
                    type: 'Attainment',
                    ordering: studySelection.substitutedByOrdering[i],
                }));
            }

            return [{ studySelection, type: 'Attainment', ordering: studySelection.ordering }];
        });

        return _.orderBy(resultSelections, 'ordering');

        function findVisibleParentIdIfHidden(selection: ModuleSelectionWithOrdering) {
            if (!selection.hidden || !selection.parentModuleId) {
                return selection.moduleId;
            }
            return findVisibleParent(selection, moduleContent).moduleId;
        }
    }

    /**
     * Return children that are before moduleSelection or after the first non child that follows moduleSelection.
     *
     * DP
     * |_CU1
     * |_CUA
     * |_ModuleSelection (hidden)
     * |_CUB
     * |_CU2
     * |_CUC
     *
     * CUA, CUB and CUC are children of ModuleSelection. This function will return CUA and CUC.
     */
    getChildrenNotDirectlyAfterModule(moduleSelection: ModuleSelectionWithOrdering, moduleContent: PlanWithOrdering): SelectionItem[] {
        const visibleParent = findVisibleParent(moduleSelection, moduleContent);
        const selectionItems = this.getChildrenVisibleUnderModule(visibleParent, moduleContent);
        const contextModuleSelectionIsParentFilter = createIsChildOfModuleFilter(moduleSelection, visibleParent, moduleContent);
        const itemsInGroupingModule = _.orderBy(
            _.filter(selectionItems, item => contextModuleSelectionIsParentFilter(item.studySelection)),
            'order',
        );
        const childrenNotInSuccession = [];
        let highestOrder = moduleSelection.ordering;
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < itemsInGroupingModule.length; i += 1) {
            const item = itemsInGroupingModule[i];
            if (moduleSelection.ordering > item.ordering) {
                childrenNotInSuccession.push(item);
            } else if (highestOrder + 1 === item.ordering) {
                highestOrder += 1;
            } else {
                childrenNotInSuccession.push(item);
            }
        }
        return childrenNotInSuccession;
    }

    /**
     * This method should be used for grouping module for who the visibility has been toggled.
     * If moduleSelection is not hidden then ordering for it and it's closest visible parent will be updated (compressed - gaps
     * will be removed).
     * If moduleSelection is hidden then children of it are inserted after it in the ordering of closest visible parent of
     * moduleSelection.
     */
    updateOrderingFor(moduleSelection: ModuleSelectionWithOrdering, moduleContent: PlanWithOrdering): void {
        const visibleParent = findVisibleParent(moduleSelection, moduleContent);
        if (!moduleSelection.hidden) {
            _.forEach(this.getChildrenVisibleUnderModule(moduleSelection, moduleContent), setOrdering);
            _.forEach(this.getChildrenVisibleUnderModule(visibleParent, moduleContent), setOrdering);
            return;
        }

        const selectionItems = this.getChildrenVisibleUnderModule(visibleParent, moduleContent);
        const isChildOfModuleFilter = createIsChildOfModuleFilter(moduleSelection, visibleParent, moduleContent);
        const partition = _.partition(selectionItems, item => isChildOfModuleFilter(item.studySelection));
        const itemsInGroupingModule = _.orderBy(partition[0], 'ordering');
        const itemsNotInGroupingModule = _.orderBy(partition[1], 'ordering');

        // put the children of grouping module after it with the help of moduleSelection.ordering
        _.forEach(itemsInGroupingModule, (item, i) => {
            setOrdering(item, moduleSelection.ordering + 1 + i);
        });

        // set non children of grouping module that are after it be after the children of grouping module
        _.forEach(itemsNotInGroupingModule, (item, i) => {
            if (item.ordering > moduleSelection.ordering) {
                setOrdering(item, moduleSelection.ordering + _.size(itemsInGroupingModule) + 1 + i);
            }
        });

        // compress ordering because there might be some gaps
        _.forEach(_.orderBy(selectionItems, 'ordering'), setOrdering);

        function setOrdering(item: SelectionItem, i: number) {
            const { studySelection } = item;
            if (isCourseUnitSelection(studySelection) && !_.isEmpty(studySelection.substitutedByOrdering)) {
                const indexOfOrdering = _.indexOf(studySelection.substitutedByOrdering, item.ordering);
                studySelection.substitutedByOrdering[indexOfOrdering] = i;
            } else {
                studySelection.ordering = i;
            }
            item.ordering = i;
        }
    }

    /**
     * This method does not assume that orderings are successive (without gaps) starting from 0. However it should always be so.
     * This will ignore if item with ordering 'orderNumber' does not exist or the next item does not exist. For example, first item
     * can not be moved upwards - the move will be ignored.
     *
     * @param contextModuleSelection The module selection that is the parent of item to be moved.
     * @param moduleContent full module content object
     * @param orderNumber ordering (or element in substitutedByOrdering) of study selection
     * @param upwards direction of the move
     */
    moveSelection(
        contextModuleSelection: ModuleSelectionWithOrdering,
        moduleContent: PlanWithOrdering,
        orderNumber: number,
        upwards: boolean,
    ) {
        // same selection might exist multiple times in this list if it has multiple substitutions: substitutedBy is not empty
        const selectionItems = this.getChildrenVisibleUnderModule(contextModuleSelection, moduleContent);
        const targetSelectionItem = _.find(selectionItems, { ordering: orderNumber });

        const otherItem = upwards
            ? _.maxBy(
                  _.filter(selectionItems, item => item.ordering < orderNumber),
                  'ordering',
              )
            : _.minBy(
                  _.filter(selectionItems, item => item.ordering > orderNumber),
                  'ordering',
              );

        if (otherItem && targetSelectionItem) {
            swapOrder(targetSelectionItem, orderNumber, otherItem.ordering);
            swapOrder(otherItem, otherItem.ordering, orderNumber);
        }

        function swapOrder(item: SelectionItem, first: number, second: number) {
            const { studySelection } = item;
            if (isCourseUnitSelection(studySelection) && !_.isEmpty(studySelection.substitutedByOrdering)) {
                const index = _.indexOf(studySelection.substitutedByOrdering, first);
                studySelection.substitutedByOrdering[index] = second;
            } else {
                studySelection.ordering = second;
            }
        }
    }

    /**
     * Creates attainment structure of attainment that represents the contextModuleSelection. Nodes will be ordered according to
     * 'ordering' properties of study selections inside moduleContent
     *
     * @param contextModuleSelection selection representing the attainment for what nodes are created
     * @param moduleContent study selections with orderings
     * @param indexedAttainments object holding necessary attainment
     * @param modulesById modules by id
     * @param courseUnitsById course units by id
     * @returns array of nodes
     */
    createNodes(
        contextModuleSelection: ModuleSelectionWithOrdering,
        moduleContent: PlanWithOrdering,
        indexedAttainments: IndexedAttainments,
        modulesById: ModulesById,
        courseUnitsById: CourseUnitsById,
    ): AttainmentNode[] {
        const getChildrenVisibleUnderModule = (moduleSelection: ModuleSelectionWithOrdering) =>
            this.getChildrenVisibleUnderModule(moduleSelection, moduleContent);

        const createNodes = (moduleSelection: ModuleSelectionWithOrdering): AttainmentNode[] =>
            _.flatMap(getChildrenVisibleUnderModule(moduleSelection), item => {
                if (item.type === 'Attainment') {
                    const attainment = this.getAttainmentForItem(item, indexedAttainments, modulesById, courseUnitsById);
                    return [{ type: 'AttainmentReferenceNode', attainmentId: _.get(attainment, 'id') }];
                }
                const gmSelection = item.studySelection;
                const childNodes = createNodes(gmSelection as ModuleSelectionWithOrdering);
                if (_.get(gmSelection, 'hidden')) {
                    return childNodes;
                }
                const name = _.get(gmSelection, 'newName');
                const groupNode: AttainmentGroupNode = { name, type: 'AttainmentGroupNode', nodes: childNodes };
                return [groupNode];
            });

        return createNodes(contextModuleSelection);
    }

    /**
     * Adds field 'attainment' to selection items having type 'Attainment'. Value of the field is the attainment.
     */
    addAttainmentsToSelectionItems(
        selectionItems: SelectionItem[],
        indexedAttainments: IndexedAttainments,
        modulesById: ModulesById,
        courseUnitsById: CourseUnitsById,
    ) {
        _.forEach(selectionItems, item => {
            if (item.type === 'GroupingModuleLike') {
                return;
            }
            item.attainment = this.getAttainmentForItem(item, indexedAttainments, modulesById, courseUnitsById);
        });
    }

    private setOrderingForCustomModuleAttainments(
        customModuleAttainmentsUnderModule: CustomModuleAttainment[],
        studySelections: StudySelection[],
        index: number,
    ) {
        let order = index;
        _.forEach(customModuleAttainmentsUnderModule, customModuleAttainment => {
            const studySelection = _.find(
                studySelections,
                selection => isCustomModuleSelection(selection) && selection.customModuleAttainmentId === customModuleAttainment.id,
            );
            if (!!studySelection) {
                order += 1;
                studySelection.ordering = order;
            }
        });
        return order;
    }

    private setOrderingForModules(
        modulesUnderModule: Module[],
        studySelections: StudySelection[],
        indexedAttainments: IndexedAttainments,
        modulesById: ModulesById,
        index: number,
        moduleContent: PlanWithOrdering,
        validatablePlan: ValidatablePlan,
    ) {
        let order = index;
        _.forEach(modulesUnderModule, module => {
            const moduleSelection = _.find(
                studySelections,
                selection => isModuleSelection(selection) && selection.moduleId === module.id,
            ) as ModuleSelectionWithOrdering;
            if (!!moduleSelection) {
                if (_.get(module, 'type') === 'GroupingModule' || !_.get(module, 'graded')) {
                    moduleSelection.newName = _.cloneDeep(module.name);
                    moduleSelection.hidden = true;
                }
                if (!getModuleAttainment(indexedAttainments, modulesById, moduleSelection.moduleId)) {
                    // enrichment
                    moduleSelection.moduleNotAttained = true;
                }
                order += 1;
                moduleSelection.ordering = order;
                order = this.initOrderForSelection(
                    moduleSelection,
                    moduleContent,
                    validatablePlan,
                    modulesById,
                    indexedAttainments,
                    moduleSelection.ordering,
                );
            }
        });
        return order;
    }

    private setOrderingForCustomStudyDrafts(
        customStudyDraftsUnderModule: CustomStudyDraft[],
        studySelections: StudySelection[],
        index: number,
    ) {
        let order = index;
        _.forEach(customStudyDraftsUnderModule, studyDraft => {
            const studySelection = _.find(
                studySelections,
                selection => isCustomStudyDraftSelection(selection) && selection.id === studyDraft.id,
            );
            if (!!studySelection) {
                order += 1;
                studySelection.ordering = order;
            }
        });
        return order;
    }

    private setOrderingForCustomCourseUnitAttainments(
        customCourseUnitAttainmentsUnderModule: CustomCourseUnitAttainment[],
        studySelections: StudySelection[],
        index: number,
    ) {
        let order = index;
        _.forEach(customCourseUnitAttainmentsUnderModule, customCourseUnitAttainment => {
            const studySelection = _.find(
                studySelections,
                selection =>
                    isCustomCourseUnitSelection(selection) && selection.customCourseUnitAttainmentId === customCourseUnitAttainment.id,
            );
            if (!!studySelection && isCustomCourseUnitSelection(studySelection)) {
                order += 1;
                studySelection.ordering = order;
            }
        });
        return order;
    }

    private setOrderingForCourseUnits(courseUnitsUnderModule: CourseUnit[], studySelections: StudySelection[], index: number) {
        let order = index;
        _.forEach(courseUnitsUnderModule, courseUnit => {
            const studySelection = _.find(
                studySelections,
                selection => isCourseUnitSelection(selection) && selection.courseUnitId === courseUnit.id,
            );
            if (!!studySelection && isCourseUnitSelection(studySelection)) {
                if (!_.isEmpty(studySelection.substitutedBy)) {
                    studySelection.substitutedByOrdering = [];
                    _.forEach(studySelection.substitutedBy, () => {
                        order += 1;
                        studySelection.substitutedByOrdering.push(order);
                    });
                } else {
                    order += 1;
                    studySelection.ordering = order;
                }
            }
        });
        return order;
    }

    // unfortunately exposed for testing
    initOrderForSelection(
        rootModuleSelection: ModuleSelectionWithOrdering,
        moduleContent: PlanWithOrdering,
        validatablePlan: ValidatablePlan,
        modulesById: ModulesById,
        indexedAttainments: IndexedAttainments,
        previousIndex: number,
    ): number {
        const flattenModule = rootModuleSelection.hidden;
        const rootModule = modulesById[rootModuleSelection.moduleId];
        const courseUnitsUnderModule = validatablePlan.getSelectedCourseUnitsUnderModule(rootModule);
        const customCourseUnitAttainmentsUnderModule = validatablePlan.getSelectedCustomCourseUnitAttainmentsUnderModule(rootModule);
        const customStudyDraftsUnderModule = validatablePlan.getSelectedCustomStudyDraftsByParentModuleId(rootModule.id);
        const modulesUnderModule = validatablePlan.getSelectedModulesUnderModule(rootModule);
        const customModuleAttainmentsUnderModule = validatablePlan.getSelectedCustomModuleAttainmentsUnderModule(rootModule);
        const studySelections = getStudySelections(moduleContent);

        let index = flattenModule ? previousIndex : -1;

        index = this.setOrderingForCourseUnits(courseUnitsUnderModule, studySelections, index);
        index = this.setOrderingForCustomCourseUnitAttainments(customCourseUnitAttainmentsUnderModule, studySelections, index);
        index = this.setOrderingForCustomStudyDrafts(customStudyDraftsUnderModule, studySelections, index);
        index = this.setOrderingForModules(
            modulesUnderModule,
            studySelections,
            indexedAttainments,
            modulesById,
            index,
            moduleContent,
            validatablePlan,
        );
        index = this.setOrderingForCustomModuleAttainments(customModuleAttainmentsUnderModule, studySelections, index);

        return flattenModule ? index : previousIndex;
    }

    private getAttainmentForItem(
        item: SelectionItem,
        indexedAttainments: IndexedAttainments,
        modulesById: ModulesById,
        courseUnitsById: CourseUnitsById,
    ): Attainment {
        const { studySelection } = item;
        let attainment;
        if (isCourseUnitSelection(studySelection)) {
            let { courseUnitId } = studySelection;
            if (!_.isEmpty(studySelection.substitutedBy)) {
                const index = _.indexOf(studySelection.substitutedByOrdering, item.ordering);
                courseUnitId = studySelection.substitutedBy[index];
            }
            attainment = getCourseUnitAttainment(indexedAttainments, courseUnitsById, courseUnitId);
        }
        if (isModuleSelection(studySelection)) {
            attainment = getModuleAttainment(indexedAttainments, modulesById, studySelection.moduleId);
        }
        let customAttainmentId = null;
        if (isCustomModuleSelection(studySelection)) {
            customAttainmentId = studySelection.customModuleAttainmentId;
        }
        if (isCustomCourseUnitSelection(studySelection)) {
            customAttainmentId = studySelection.customCourseUnitAttainmentId;
        }
        if (customAttainmentId) {
            // custom course unit / module attainment selection
            attainment = _.get(indexedAttainments.byId, customAttainmentId);
        }
        if (isCustomStudyDraftSelection(studySelection)) {
            // custom study draft
            attainment = _.get(indexedAttainments.byCustomStudyDraftId, studySelection.id);
        }
        if (!attainment) {
            // If we encounter this, then it likely that eventually we run into problems. Example: "Cannot read property 'id' of undefined"
            this.logService.error('Angular', { item, message: 'Could not find attainment for selection item' });
        }
        return attainment;
    }
}

/**
 * Assumptions:
 *  contextModuleSelection is a (recursive) child of visibleParent.
 *  Callback is called with a (recursive) child of visibleParent.
 *
 * Callback will tell if contextModuleSelection is ancestor of selection by recursively checking parent modules.
 * Callback short circuits when visibleParent is encountered. NPE will be thrown if latter assumption does not hold.
 */
function createIsChildOfModuleFilter(
    contextModuleSelection: ModuleSelectionWithOrdering,
    visibleParent: ModuleSelectionWithOrdering,
    moduleContent: PlanWithOrdering,
): (x: StudySelection) => boolean {
    function isContextModuleSelectionOneOfParents(selection: StudySelection): boolean {
        const { parentModuleId } = selection;
        if (parentModuleId === contextModuleSelection.moduleId) {
            return true;
        }
        // no need to iterate until education
        if (parentModuleId === visibleParent.moduleId) {
            return false;
        }
        return isContextModuleSelectionOneOfParents(_.find(moduleContent.moduleSelections, { moduleId: parentModuleId }));
    }

    return isContextModuleSelectionOneOfParents;
}

function getStudySelections(moduleContent: PlanWithOrdering): StudySelection[] {
    return _.compact(
        [
            moduleContent.courseUnitSelections,
            moduleContent.customCourseUnitAttainmentSelections,
            moduleContent.customStudyDrafts,
            moduleContent.moduleSelections,
            moduleContent.customModuleAttainmentSelections,
        ].flat(),
    );
}

function findVisibleParent(selection: StudySelection, moduleContent: PlanWithOrdering): ModuleSelectionWithOrdering {
    const parent = _.find(moduleContent.moduleSelections, { moduleId: selection.parentModuleId });
    return parent.hidden ? findVisibleParent(parent, moduleContent) : parent;
}

function getModuleAttainment(indexedAttainments: IndexedAttainments, modulesById: ModulesById, moduleId: OtmId) {
    return _.get(indexedAttainments.byModuleGroupId, _.get(modulesById, [moduleId, 'groupId']));
}

function getCourseUnitAttainment(indexedAttainments: IndexedAttainments, courseUnitsById: CourseUnitsById, courseUnitId: OtmId) {
    return _.get(indexedAttainments.byCourseUnitGroupId, _.get(courseUnitsById, [courseUnitId, 'groupId']));
}
